import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
//import { useMemo } from 'react';
import useAuthInit from './hooks/useAuthInit';
import type { AppProps } from './types/app.types';

import GuestLayout from './layouts/GuestLayout';
import AppLayout from './layouts/AppLayout';
import GuestRoute from './components/GuestRoute';
import ProtectedRoute from './components/ProtectedRoute';

import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, lazy, Suspense } from 'react';
//import { setNavigate } from './services/authService';
import { getAuthenticatedHomeRoute } from './utils/domain';
import type {
    LoginPageProps,
    ForgotPasswordPageProps,
    ForgotPasswordLoginPageProps,
    ForgotPasswordEmailPageProps,
    ResetPasswordPageProps,
    RequestAccountPageProps,
    SsoLoginPageProps,
} from './types/auth.types';

//import Error500 from './pages/errors/500';

const Login = lazy(() => import('./pages/auth/Login'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ForgotPasswordLogin = lazy(() => import('./pages/auth/ForgotPasswordLogin'));
const ForgotPasswordEmail = lazy(() => import('./pages/auth/ForgotPasswordEmail'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const RequestAccount = lazy(() => import('./pages/auth/RequestAccount'));
const SsoLogin = lazy(() => import('./pages/auth/SsoLogin'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ChooseTenant = lazy(() => import('./pages/ChooseTenant'));
const TestPage = lazy(() => import('./pages/TestPage'));
const Testing = lazy(() => import('./pages/Testing'));
const CustomObjectList = lazy(() => import('./pages/CustomObjectList'));
const CreateViewPage = lazy(() => import('./pages/custom-object/CreateViewPage'));
const ObjectListPage = lazy(() => import('./pages/custom-object/ObjectListPage'));
const EditViewPage = lazy(() => import('./pages/custom-object/EditViewPage'));
const DrcPersonaPage = lazy(() => import('./pages/avatar/DrcPersonaPage'));
const ClientPersonalizationPage = lazy(() => import('./pages/avatar/ClientPersonalizationPage'));
const LearningAdvisorPage = lazy(() => import('./pages//portal/LearningAdvisor'));
const DrcVideoPage = lazy(() => import('./pages/ai/DrcVideo'));
const CustomRenderPage = lazy(() => import('./pages/custom-object/CustomRenderPage'));
const SearchPage = lazy(() => import('./pages/global-search/search-page'));
const EmailMessagePage = lazy(() => import('./pages/custom-object/EmailMessagePage'));
const EmailActionPage = lazy(() => import('./pages/custom-object/EmailActionPage'));
const GroupDetailPage = lazy(() => import('./pages/custom-object/GroupDetailPage'));
const Error403 = lazy(() => import('./pages/errors/Forbidden'));
const Error404 = lazy(() => import('./pages/errors/NotFound'));
const SurveyPage = lazy(() => import('./pages/custom-object/SurveyPage'));
const ObjectHistoryListPage = lazy(() => import('./pages/custom-object/ObjectHistoryListPage'));
const TranslationOverrideManager = lazy(() => import('./pages/translations/TranslationOverrideManager'));

const ApexForgeApp = lazy(() => import('@/modules/apex-forge/ApexForgeApp').then((m) => ({ default: m.ApexForgeApp })));
const ReportingApp = lazy(() => import('@/modules/reporting/ReportingApp').then((m) => ({ default: m.ReportingApp })));
// const CaseManagementApp = lazy(() =>
//     import('@/modules/case-management/CaseManagementApp').then((m) => ({ default: m.CaseManagementApp })),
// );
export default function App(props: AppProps) {
    useAuthInit(); //only here
    const authenticatedHomeRoute = getAuthenticatedHomeRoute();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // pass navigate to your authService
        //setNavigate(navigate);
    }, [navigate]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const incomingHideHeader = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const queryHideHeader = params.get('hide_header');

        if (queryHideHeader !== null) {
            return queryHideHeader === '1' || queryHideHeader === 'true';
        }

        const propValue = props.hide_header;
        return propValue === '1' || propValue === 1 || propValue === true;
    }, [location.search, props]);

    return (
        <Suspense fallback={<div className="p-6 text-sm text-slate-500">Loading…</div>}>
            <Routes>
                {/* Guest */}
                <Route element={<GuestRoute />}>
                    <Route element={<GuestLayout />}>
                        <Route index element={<Login {...(props as LoginPageProps)} />} />
                        <Route path="login" element={<Login {...(props as LoginPageProps)} />} />

                        <Route path="ssologin" element={<SsoLogin {...(props as SsoLoginPageProps)} />} />

                        <Route
                            path="forgot-password"
                            element={<ForgotPassword {...(props as ForgotPasswordPageProps)} />}
                        />

                        <Route
                            path="forgot-password-login"
                            element={<ForgotPasswordLogin {...(props as ForgotPasswordLoginPageProps)} />}
                        />

                        <Route
                            path="forgot-password-email"
                            element={<ForgotPasswordEmail {...(props as ForgotPasswordEmailPageProps)} />}
                        />

                        <Route
                            path="reset-password/:token"
                            element={<ResetPassword {...(props as ResetPasswordPageProps)} />}
                        />

                        <Route
                            path="request-account"
                            element={<RequestAccount {...(props as RequestAccountPageProps)} />}
                        />
                    </Route>
                </Route>

                {/* Protected */}
                <Route element={<ProtectedRoute />}>
                    <Route index element={<Navigate to={authenticatedHomeRoute} replace />} />
                    <Route path="choose-tenant" element={<ChooseTenant />} />
                    <Route path="/drc/video-twin" element={<DrcVideoPage {...props} />} />
                    <Route element={<AppLayout hideHeader={incomingHideHeader} logoUrl={props.logoUrl} />}>
                        <Route path="dashboard" element={<Dashboard />} />
                        <Route
                            path="portal-selection/learning-advisor"
                            element={
                                <LearningAdvisorPage
                                    drcs={props.drcs}
                                    advisor_options_label={props.advisor_options_label}
                                    is_client_facing={props.is_client_facing}
                                />
                            }
                        />
                        <Route path="testpage-api" element={<TestPage />} />
                        <Route path="/translations" element={<TranslationOverrideManager />} />
                        <Route path="testing-api" element={<Testing />} />
                        <Route path="/object-list" element={<ObjectListPage />} />
                        <Route path="/object-history" element={<ObjectHistoryListPage />} />
                        <Route path="/custom-objects-page" element={<ObjectListPage />} />
                        <Route path="/new-view/create/:objectId" element={<CreateViewPage />} />
                        <Route path="/new-view/:action/:objectId/:viewId" element={<EditViewPage />} />

                        <Route path="/search" element={<SearchPage />} />

                        <Route path="/custom-object/layout/:object/default/list" element={<CustomObjectList />} />
                        <Route path="/drc-persona" element={<DrcPersonaPage />} />
                        <Route path="/client-personalization" element={<ClientPersonalizationPage />} />
                        <Route
                            path="/custom-object/layout/:object/:layout_id/:record/survey"
                            element={<SurveyPage />}
                        />
                        <Route
                            path="/custom-object/layout/:object/default/:recordId/:action"
                            element={<CustomRenderPage />}
                        />
                        <Route path="/custom-object/layout/:object/:layout_id/:action" element={<CustomRenderPage />} />
                        <Route path="/custom-object/layout/email/:action/:recordId?" element={<EmailActionPage />} />
                        <Route
                            path="/custom-object/layout/EmailMessage/standard/:record/view"
                            element={<EmailMessagePage />}
                        />
                        <Route
                            path="/custom-object/layout/Group/default/:recordId/:action"
                            element={<GroupDetailPage />}
                        />
                        <Route path="/custom-object/layout/Group/:layout_id/:action" element={<GroupDetailPage />} />
                        <Route
                            path="/apex-forge/*"
                            element={
                                <Suspense fallback={<div className="p-6 text-sm text-slate-500">Loading…</div>}>
                                    <ApexForgeApp />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/reports/*"
                            element={
                                <Suspense fallback={<div className="p-6 text-sm text-slate-500">Loading…</div>}>
                                    <ReportingApp fiscalYearStartMonth={props.fiscalYearStartMonth} />
                                </Suspense>
                            }
                        />
                        {/* <Route
                        path="/case-management/*"
                        element={
                            <Suspense fallback={<div className="p-6 text-sm text-slate-500">Loading…</div>}>
                                <CaseManagementApp />
                            </Suspense>
                        }
                    /> */}
                    </Route>
                </Route>

                {/* 404 */}
                {/* Error Pages */}
                <Route path="/403" element={<Error403 />} />
                {/* <Route path="/500" element={<Error500 />} /> */}

                {/* 404 Route */}
                <Route path="*" element={<Error404 />} />
                {/* <Route path="*" element={<div className="p-6 text-sm text-slate-600">404 Not Found</div>} /> */}
            </Routes>
        </Suspense>
    );
}
